import React from 'react';
import { Award, Users, Shield, Target, TrendingUp, Heart } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo } from '../mock';

export const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Safety First',
      description: 'We prioritize safety in every operation with strict protocols and certified equipment.'
    },
    {
      icon: Target,
      title: 'Reliability',
      description: 'Consistent, on-time service delivery that our clients can depend on.'
    },
    {
      icon: Heart,
      title: 'Customer Focus',
      description: 'Building lasting relationships through exceptional service and support.'
    },
    {
      icon: TrendingUp,
      title: 'Continuous Improvement',
      description: 'Investing in modern equipment and training for superior service quality.'
    }
  ];

  return (
    <div className="min-h-screen bg-burgundy-deep">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-burgundy-dark grain-overlay relative">
        <div className="container mx-auto text-center relative z-10">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">
            About NJK Logistic Pte Ltd
          </h1>
          <p className="text-lg text-white/70 max-w-3xl mx-auto">
            {companyInfo.tagline} - Your trusted partner in heavy vehicle logistics
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_6ad06afe-da3b-46ca-832e-3147a77af072/artifacts/qs428xee_image.png"
                  alt="NJK Logistic Pte Ltd fleet"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="space-y-6">
              <div className="inline-block">
                <span className="bg-gold/15 text-gold px-4 py-2 rounded-full text-sm font-medium">
                  Our Story
                </span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white">
                Building on a Decade of Excellence
              </h2>
              <p className="text-lg text-white/70 leading-relaxed">
                With over 10 years of experience in the heavy vehicle logistics industry, NJK Logistic Pte Ltd was established to provide professional, reliable, and efficient solutions for all heavy lifting and transportation needs.
              </p>
              <p className="text-lg text-white/70 leading-relaxed">
                Our journey began with a simple mission: to deliver swift, secure, and reliable logistics services. Today, we're proud to operate a modern fleet of lorry cranes, tailgate lorries, and trailers, backed by a team of certified professionals who share our commitment to excellence.
              </p>
              <p className="text-lg text-white/70 leading-relaxed">
                From small-scale projects to large industrial operations, we've built our reputation on delivering results that exceed expectations. Our decade of industry experience has taught us that success comes from combining the right equipment, skilled operators, and unwavering commitment to safety and customer satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-4 bg-burgundy-dark grain-overlay relative">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border border-gold/25 bg-burgundy-deep relative z-10">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gold/15 rounded-xl flex items-center justify-center mb-6">
                  <Target className="text-gold" size={32} />
                </div>
                <h3 className="font-display text-2xl font-bold text-white mb-4">Our Mission</h3>
                <p className="text-white/70 leading-relaxed">
                  To provide industry-leading heavy vehicle logistics solutions that prioritize safety, efficiency, and customer satisfaction. We strive to be the most reliable partner for businesses across Singapore, delivering exceptional service with every project.
                </p>
              </CardContent>
            </Card>
            <Card className="border border-gold/25 bg-burgundy-deep relative z-10">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gold/15 rounded-xl flex items-center justify-center mb-6">
                  <Award className="text-gold" size={32} />
                </div>
                <h3 className="font-display text-2xl font-bold text-white mb-4">Our Vision</h3>
                <p className="text-white/70 leading-relaxed">
                  To be Singapore's most trusted heavy vehicle logistics company, recognized for our professionalism, innovation, and unwavering commitment to excellence. We aim to set new standards in the industry through continuous improvement and investment in our people and equipment.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              The principles that guide every decision and action at NJK Logistic Pte Ltd
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="bg-burgundy-dark border border-gold/20 hover:border-gold/50 transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gold/15 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-gold" size={28} />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                    <p className="text-white/65 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 bg-burgundy-dark grain-overlay relative">
        <div className="container mx-auto text-center relative z-10">
          <Users className="text-white mx-auto mb-6" size={48} />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Our Professional Team
          </h2>
          <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
            Every member of our team is certified, trained, and committed to delivering safe and efficient service. From our operators to our support staff, we're united in our mission to exceed your expectations.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-white">
              <p className="text-3xl font-bold mb-2">25+</p>
              <p className="text-white/70">Certified Operators</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-white">
              <p className="text-3xl font-bold mb-2">100%</p>
              <p className="text-white/70">Safety Certified</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-white">
              <p className="text-3xl font-bold mb-2">24/7</p>
              <p className="text-white/70">Support Available</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};